import type { IGenre, IInfoGuide, SongType } from 'services/api/entities';
import type {
  BundleResponse,
  FormulaResponse,
  Instrument,
  SongStructure,
  VegaSongResponse,
} from 'services/api/responses/responseTypes';
import { EnergyType } from 'services/api/entities';
import { type UserLoop as UserLoopValue } from 'types/userLoop';

export enum FormulaFields {
  Duration = 'duration',
  Instruments = 'instruments',
  GenreBlend = 'genre_blend',
  Energy = 'energy',
  Structure = 'structure_id',
  Bpm = 'bpm',
  Key = 'key',
  UserLoop = 'user_loop',
}

export enum KeyRoot {
  CSharp = 'C#/Db',
  DSharp = 'D#/Eb',
  FSharp = 'F#/Gb',
  GSharp = 'G#/Ab',
  BFlat = 'A#/Bb',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  A = 'A',
  B = 'B',
}

export enum KeyQuality {
  MINOR = 'minor',
  MAJOR = 'major',
}

export enum BundleType {
  ADVANCED = 'ADVANCED',
  TEXT2MUSIC = 'TEXT2MUSIC',
}

export enum PromptType {
  AdvancedFormula = 'generator',
  TextToMusic = 'text-to-music',
}

export interface GenreBlend {
  field: FormulaFields.GenreBlend;
  value: IGenre['name'];
}

export interface Energy {
  field: FormulaFields.Energy;
  value: EnergyType;
}

export interface Bpm {
  field: FormulaFields.Bpm;
  value: number;
}

export interface Duration {
  field: FormulaFields.Duration;
  value: number;
}

export interface Instruments {
  field: FormulaFields.Instruments;
  value: Partial<Record<Instrument, number>>;
}

export interface Structure {
  field: FormulaFields.Structure;
  value: number | null;
}

export interface Key {
  field: FormulaFields.Key;
  value: {
    root: KeyRoot;
    quality: KeyQuality;
  };
}

export interface UserLoop {
  field: FormulaFields.UserLoop;
  value: UserLoopValue;
}

export type FormulaField =
  | Duration
  | Instruments
  | GenreBlend
  | Energy
  | Structure
  | Bpm
  | Key
  | UserLoop;
export type FormulaFieldType = FormulaField['field'];
export type FormulaFieldValue = FormulaField['value'];
export type Formula = Array<FormulaField>;

interface BasePrompt {
  id: string;
  promptType: PromptType;
}

export interface AdvancedFormulaPrompt extends BasePrompt {
  promptType: PromptType.AdvancedFormula;
  primaryGenre: IGenre['name'];
  microGenre: IGenre['name'] | null;
  formula: Formula;
}

export interface TextPrompt extends BasePrompt {
  promptType: PromptType.TextToMusic;
  text: string;
  userLoop?: Extract<FormulaField, UserLoop>;
  formula: Formula;
}

export type AIGeneratorPrompt = AdvancedFormulaPrompt | TextPrompt;

export interface VegaSong
  extends Pick<VegaSongResponse, 'id' | 'title' | 'duration'> {
  songType: SongType.AI_GENERATED;
  musicFilePath: string;
  xmlFilePath: string;
  wavFilePath: string;
  createdAt: string;
  isInLibrary: boolean;
  isPreview: boolean;
  formula: Formula;
  genre: string;
}

export enum BundleSongStatus {
  SUCCESS = 'SUCCESS',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
}

export interface BundleSongSuccess {
  status: BundleSongStatus.SUCCESS;
  song: VegaSong;
}

export interface BundleSongLoading {
  status: BundleSongStatus.LOADING;
}

export interface BundleSongError {
  status: BundleSongStatus.ERROR;
  error: Error;
}

export type BundleSongState =
  | BundleSongSuccess
  | BundleSongLoading
  | BundleSongError;

interface BaseBundle {
  id: string;
  bundleType: BundleType;
  isPreview: boolean | null;
  createdAt: BundleResponse['created_at'];
  createdAtTimestamp: number;
  capacity: BundleResponse['capacity'];
  isFinished: BundleResponse['is_finished'];
  genre: string;
  formula: Formula;
  songs: BundleSongState[];
}

export interface AdvancedBundle extends BaseBundle {
  bundleType: BundleType.ADVANCED;
}

export interface TextBundle extends BaseBundle {
  bundleType: BundleType.TEXT2MUSIC;
  prompt: NonNullable<FormulaResponse['prompt']>;
}

export type VegaBundle = AdvancedBundle | TextBundle;

export type FormulaRecord = Partial<{
  [FieldName in FormulaField['field']]: Extract<
    FormulaField,
    { field: FieldName }
  >['value'];
}>;
export interface StoreSongStructure extends Omit<SongStructure, 'image_url'> {
  imageUrl: string;
}

export type StoreSongStructures = Array<StoreSongStructure>;

export interface BundlesPage {
  items: VegaBundle[];
  pagination: {
    currentPage: number;
    lastPage: number;
    itemsPerPage: number;
    itemsCount: number;
    totalPages: number;
    totalItems: number;
  };
}

export type AIStore = {
  genres: Array<IGenre>;
  primaryGenre: IGenre['name'] | null;
  microGenre: IGenre['name'] | null;
  currentTrack: VegaSong['id'] | null;
  tracks: Record<string, Readonly<VegaSong>>;
  formula: Formula;
  isFormulaDrawerOpened: boolean;
  infoGuide: Array<IInfoGuide> | null;
  structures: StoreSongStructures;
  promptType: PromptType;
  textPrompt: string;
  placeholderPrompt: string;
};
